import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import _isEmpty from 'lodash/isEmpty';
import _isString from 'lodash/isString';
import { useDispatch, useSelector } from 'react-redux';

import { addBusinessFile, flagBusinessSaved } from '../../../../../actions/business';
import API from '../../../../../libs/api';
import ErrorHandler, { getErrorMessage } from '../../../../../libs/errors';
import { selectBusiness } from '../../../../../selectors';
import { ImagePropType } from '../../../custom-pages/types/image-block.types';
import useAsyncEffect from '../../../foundation/hooks/use-async-effect';
import FileEditorModal from '../FileEditorModal/FileEditorModal';
import FilesManagementModal from '../FilesManagementModal/FilesManagementModal';

const propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  initialSelectedFiles: PropTypes.arrayOf(ImagePropType),
  minFilesAllowed: PropTypes.number,
  maxFilesAllowed: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionButtonText: PropTypes.string,
  uploadSourceType: PropTypes.string,
};

const defaultProps = {
  minFilesAllowed: null,
  initialSelectedFiles: [],
  actionButtonText: 'Save',
  uploadSourceType: '',
};

export default function FilesManagementModalProvider({
  open,
  setOpen,
  initialSelectedFiles,
  minFilesAllowed,
  maxFilesAllowed,
  onSubmit,
  actionButtonText,
  uploadSourceType,
}) {
  const [files, setFiles] = useState([]);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(initialSelectedFiles);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState();

  const { id: businessId, type: businessType } = useSelector(selectBusiness);
  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedFiles(initialSelectedFiles);
  }, [initialSelectedFiles]);

  function showFileModal() {
    setOpenFileModal(true);
  }

  function closeFileModal() {
    setOpenFileModal(false);
  }

  function closeImagesModal() {
    closeFileModal();
    setOpen(false);
  }

  async function getBusinessFiles(searchValueOverride) {
    try {
      setIsLoading(true);
      const finalSearchValue = _isString(searchValueOverride) ? searchValueOverride : searchValue;
      const { data } = await API.getBusinessFiles(businessType, businessId, {
        search: finalSearchValue,
        order_by: '-created_at',
      });
      setFiles(data);
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  function onFileClick(file) {
    const { id: fileId } = file;

    if (_isEmpty(selectedFiles)) {
      setSelectedFiles([file]);
      return;
    }

    const fileIndex = selectedFiles.findIndex(({ id: currentId }) => currentId === fileId);

    if (fileIndex === -1) {
      setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, file]);
      return;
    }

    setSelectedFiles((prevSelectedFiles) => {
      return prevSelectedFiles.filter(({ id: currentId }) => currentId !== fileId);
    });
  }

  async function onFileSearch(e, target) {
    const { value = '' } = target || {};
    setSearchValue(value);
    setIsSearching(true);
    await getBusinessFiles(value);
    setIsSearching(false);
  }

  function onFileEdit(file) {
    const { id: fileId } = file;
    const index = files && files.findIndex(({ id: currentId }) => currentId === fileId);
    const businessFile = index !== false && index >= 0 && { ...files[index], index };

    setSelectedFile(businessFile);
    showFileModal();
    dispatch(flagBusinessSaved(true));
  }

  async function addBusinessFiles(filesPayload) {
    const filesResults = await Promise.all(
      filesPayload.map((payload) => API.createBusinessFile(businessType, businessId, payload)),
    );
    filesResults.map(({ data }) => dispatch(addBusinessFile(data)));
    await getBusinessFiles();
    dispatch(flagBusinessSaved(true));
  }

  async function onFileUpload(uploadedFiles) {
    setIsLoading(true);

    const filesPayload = uploadedFiles.map((file) => ({
      file,
      filename: file.name,
      description: '',
      source: uploadSourceType,
    }));

    try {
      await addBusinessFiles(filesPayload);
      dispatch(flagBusinessSaved(true));
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  useAsyncEffect(async () => {
    if (!open) {
      return;
    }
    setSearchValue('');
    await getBusinessFiles('');
  }, [open]);

  async function handleSubmit() {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedFiles);
      setSelectedFiles([]);
      closeImagesModal();
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <FilesManagementModal
        open={open}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        onClose={closeImagesModal}
        files={files}
        selectedFiles={selectedFiles}
        minFilesAllowed={minFilesAllowed}
        maxFilesAllowed={maxFilesAllowed}
        onFileClick={onFileClick}
        onFileSearch={onFileSearch}
        onFileEdit={onFileEdit}
        onSubmit={handleSubmit}
        searchValue={searchValue}
        isSearching={isSearching}
        onUpload={onFileUpload}
        onFilesUploaded={getBusinessFiles}
        setError={setError}
        error={error}
        actionButtonText={actionButtonText}
      />

      {openFileModal && selectedFile && (
        <FileEditorModal
          open={openFileModal}
          onClose={closeFileModal}
          businessFile={selectedFile}
        />
      )}
    </>
  );
}

FilesManagementModalProvider.propTypes = propTypes;
FilesManagementModalProvider.defaultProps = defaultProps;
